import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HeartHandshake, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/api";
import { NAV_LINKS } from "@/lib/data";

function useNavGo() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (link) => {
    window.dispatchEvent(new Event("wnt:close-nav"));
    if (link.href) { navigate(link.href); return; }
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(link.id), 450);
    } else {
      scrollToId(link.id);
    }
  };
}

export default function Nav({ onDonate }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const go = useNavGo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    const close = () => setOpen(false);
    window.addEventListener("wnt:close-nav", close);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wnt:close-nav", close);
    };
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-pine-deep/90 backdrop-blur-xl shadow-lg shadow-pine-deep/30" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-20 items-center justify-between">
            <button data-testid="nav-home-link" onClick={() => { window.dispatchEvent(new Event("wnt:close-nav")); if (window.location.pathname !== "/") { window.location.href = "/"; } else { window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0); } }} className="flex items-center gap-3">
              <img src="/logo.png" alt="Wag n Tails logo" className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-gold/70 shadow-lg" />
              <span className="hidden sm:block font-serif text-lg leading-tight text-sand">
                Wag <span className="text-gold-soft italic">n</span> Tails
                <span className="block font-mono text-[9px] uppercase tracking-[0.28em] text-sand/60">Mountain Retreat Rescue</span>
              </span>
            </button>

            <nav className="hidden md:flex items-center gap-7">
              {NAV_LINKS.map((l) => (
                <button key={l.label} data-testid={`nav-link-${(l.id || l.href).replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} onClick={() => go(l)}
                  className="text-sm font-medium text-sand/80 hover:text-gold-soft transition-colors">
                  {l.label}
                </button>
              ))}
              <button data-testid="nav-donate-cta" onClick={onDonate}
                className="group flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-charcoal transition-all hover:bg-gold-soft hover:scale-[1.03] active:scale-95">
                <HeartHandshake className="h-4 w-4 transition-transform group-hover:rotate-12" />
                Donate Now
              </button>
              <a data-testid="admin-portal-link" href="/admin" title="Admin Portal"
                className="rounded-full border border-sand/25 p-2 text-sand/60 hover:text-gold-soft hover:border-gold-soft/60 transition-colors">
                <Menu className="h-4 w-4" />
              </a>
            </nav>

            <button data-testid="nav-mobile-toggle" className="md:hidden text-sand p-2" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-16 z-40 md:hidden bg-pine-deep/97 backdrop-blur-xl border-t border-sand/10 px-6 py-6"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button key={l.label} data-testid={`nav-mobile-link-${(l.id || l.href).replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} onClick={() => go(l)}
                  className="py-3 text-left font-serif text-2xl text-sand border-b border-sand/10">
                  {l.label}
                </button>
              ))}
              <button data-testid="nav-mobile-donate-cta" onClick={() => { setOpen(false); onDonate(); }}
                className="mt-4 rounded-full bg-gold py-3.5 font-bold text-charcoal">
                Donate Now
              </button>
              <a data-testid="admin-portal-link-mobile" href="/admin" className="mt-3 text-center font-mono text-xs uppercase tracking-widest text-sand/50">
                Admin Portal
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
